import React from 'react';

import { ResizableHandle, useMinSize } from '@bringg-frontend/resizable-panel';

import ColumnSection from './column-section/column-section';
import DetailColumn, { CustomStyle } from './detail-column/detail-column';
import { ColumnDataBasic, ViewColumn } from '../../types';

const validateMinSize = (minSize: number, columnsNumber: number) => {
	if (minSize * columnsNumber > 100) {
		return Math.floor(100 / columnsNumber);
	}

	return minSize;
};

export const panelSelector = (groupId: string) => `[data-panel-group-id="${groupId}"]`;

interface Props<T extends ColumnDataBasic, G extends ColumnDataBasic | void = void> {
	groupId: string;
	columns: ViewColumn<T, G>[];
	data: (T | G)[];
	rowCount: number;
	customStyle?: CustomStyle;
	columnWithScrollBar?: string;
	isGroupFn?: (row: T | G) => boolean;
	isRowValid?: (row: T | G) => boolean;
}

const DetailsColumns = <T extends ColumnDataBasic, G extends ColumnDataBasic | void = void>({
	groupId,
	columns,
	data,
	rowCount,
	columnWithScrollBar,
	customStyle,
	isGroupFn,
	isRowValid
}: Props<T, G>) => {
	const minSize = useMinSize(24, panelSelector(groupId));

	return (
		<>
			{columns.map((column, index) => (
				<React.Fragment key={column.id}>
					<ColumnSection
						defaultSize={column.size}
						sectionName={column.Header as string}
						minSize={validateMinSize(minSize, columns.length)}
						isLastColumn={columns.length - 1 === index}
						order={index + 1}
					>
						<DetailColumn<T, G>
							rowData={data}
							rowCount={rowCount}
							column={column}
							columnIndex={index}
							withScrollBar={column.id === columnWithScrollBar}
							customStyles={customStyle}
							isGroupFn={isGroupFn}
							data-test-id={`${groupId}-${column.id}`}
							isRowValid={isRowValid}
						/>
					</ColumnSection>
					{index !== columns.length - 1 && <ResizableHandle />}
				</React.Fragment>
			))}
		</>
	);
};

export default DetailsColumns;
